import { useEffect, useState, type RefObject } from 'react';

/**
 * Whether `ref`'s element has come into view (once true, it stays true).
 *
 * Used by `MarkdownContent` to gate trailing content scanners: a link preview
 * costs a fetch, and a transcript can hold hundreds of links the reader never
 * scrolls to. Watching the markdown container — an element that is in the DOM
 * regardless — keeps that gate free of any placeholder markup, which matters
 * because markdown blocks zero their bottom margin with `last:mb-0` and any
 * element rendered after the prose brings it back.
 *
 * `enabled` skips the observer entirely for the common case of a block with
 * nothing to defer. Without `IntersectionObserver` (jsdom, older browsers)
 * it reports true, so the gate fails open rather than hiding content.
 */
export function useInView(ref: RefObject<Element | null>, enabled: boolean): boolean {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!enabled || inView) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '150px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, enabled, inView]);

  return inView;
}
